'use client'

import Link from 'next/link'
import { useTheme } from '@/contexts/ThemeContext'
import { ArrowLeft, AlertTriangle, Phone, CheckCircle, Eye, Activity } from 'lucide-react'

export default function BlackoutPage() {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  return (
    <div className={`min-h-screen py-4 xxs:py-3 px-3 xxs:px-2 ${
      isDark ? 'bg-gray-900' : 'bg-gradient-to-b from-red-50 to-white'
    }`}>
      <div className="max-w-3xl mx-auto">
        <Link
          href="/safety"
          className={`inline-flex items-center gap-1 mb-4 xxs:mb-3 text-sm xxs:text-xs ${
            isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4 xxs:w-3.5 xxs:h-3.5" />
          안전 가이드 목록
        </Link>

        <article className={`rounded-xl shadow-lg overflow-hidden ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
          {/* 헤더 */}
          <div className={`p-6 xxs:p-4 ${isDark ? 'bg-red-900/30' : 'bg-red-50'}`}>
            <div className="flex items-center gap-3 xxs:gap-2">
              <div className="p-2 xxs:p-1.5 rounded-lg bg-red-500">
                <AlertTriangle className="w-6 h-6 xxs:w-5 xxs:h-5 text-white" />
              </div>
              <h1 className={`text-2xl xxs:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                블랙아웃 대처법
              </h1>
            </div>
            <div className={`mt-3 xxs:mt-2 p-3 xxs:p-2 rounded-lg border-l-4 border-red-500 ${
              isDark ? 'bg-red-900/30' : 'bg-red-100'
            }`}>
              <p className={`text-sm xxs:text-xs font-medium ${isDark ? 'text-red-300' : 'text-red-800'}`}>
                블랙아웃은 프리다이빙의 가장 위험한 상황입니다. 반드시 숙지하세요!
              </p>
            </div>
          </div>

          <div className="p-6 xxs:p-4 space-y-6 xxs:space-y-4">
            {/* 블랙아웃이란? */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-3 xxs:mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                블랙아웃이란?
              </h2>
              <p className={`text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                뇌의 산소 부족으로 인한 일시적인 의식 상실 상태입니다.
                과호흡이나 무리한 다이빙으로 발생할 수 있습니다.
              </p>
            </div>

            {/* 전조증상 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                블랙아웃 전조증상
              </h2>
              <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-orange-900/20' : 'bg-orange-50'}`}>
                <div className="space-y-2 xxs:space-y-1.5">
                  {[
                    { icon: Eye, text: '시야가 좁아지거나 터널 비전' },
                    { icon: Activity, text: '팔다리에 힘이 빠짐' },
                    { icon: Activity, text: '근육 경련 또는 떨림' },
                    { icon: AlertTriangle, text: '혼란스러운 정신 상태' },
                    { icon: Activity, text: '비정상적인 움직임' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <item.icon className={`w-4 h-4 xxs:w-3.5 xxs:h-3.5 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                      <span className={`text-sm xxs:text-xs ${isDark ? 'text-orange-200' : 'text-orange-800'}`}>
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 응급 대처법 */}
            <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                응급 대처법
              </h2>
              <div className="space-y-3 xxs:space-y-2">
                {[
                  {
                    step: '1단계',
                    title: '즉시 수면으로',
                    items: ['다이버를 최대한 빨리 수면으로 끌어올림', '머리를 물 밖으로 유지', '기도 확보 (턱을 들어올림)'],
                    color: 'cyan'
                  },
                  {
                    step: '2단계',
                    title: '얼굴에 물 뿌리기',
                    items: ['얼굴에 물을 뿌려 자극', '"괜찮아?" 큰 소리로 호명', '마스크 제거'],
                    color: 'blue'
                  },
                  {
                    step: '3단계',
                    title: '의식 회복 확인',
                    items: ['대부분 10초 이내 회복', '회복 후에도 물 밖으로 대피', '최소 30분 휴식', '당일 다이빙 중단'],
                    color: 'green'
                  },
                ].map((stage, idx) => (
                  <div key={idx} className={`p-3 xxs:p-2 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-white'}`}>
                    <span className={`text-xs xxs:text-[10px] font-medium ${
                      stage.color === 'cyan' ? (isDark ? 'text-blue-400' : 'text-blue-600') :
                      stage.color === 'blue' ? (isDark ? 'text-blue-400' : 'text-blue-600') :
                      (isDark ? 'text-green-400' : 'text-green-600')
                    }`}>
                      {stage.step}
                    </span>
                    <h3 className={`font-bold text-sm xxs:text-xs mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {stage.title}
                    </h3>
                    <ul className={`space-y-1 text-xs xxs:text-[10px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {stage.items.map((item, i) => (
                        <li key={i} className="flex items-center gap-1.5">
                          <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                {/* 긴급 단계 */}
                <div className={`p-3 xxs:p-2 rounded-lg border-l-4 border-red-500 ${isDark ? 'bg-red-900/30' : 'bg-red-100'}`}>
                  <span className={`text-xs xxs:text-[10px] font-medium ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                    4단계
                  </span>
                  <h3 className={`font-bold text-sm xxs:text-xs mb-2 ${isDark ? 'text-red-300' : 'text-red-900'}`}>
                    의식 회복 안 될 때
                  </h3>
                  <ul className={`space-y-1 text-xs xxs:text-[10px] ${isDark ? 'text-red-200' : 'text-red-800'}`}>
                    <li className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> 즉시 119 신고</li>
                    <li className="flex items-center gap-1.5"><CheckCircle className="w-3 h-3" /> 수상 안전 요원 호출</li>
                    <li className="flex items-center gap-1.5"><CheckCircle className="w-3 h-3" /> CPR 준비 (필요시)</li>
                    <li className="flex items-center gap-1.5"><CheckCircle className="w-3 h-3" /> 병원 후송</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 예방법 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                예방법
              </h2>
              <div className="space-y-2 xxs:space-y-1.5">
                {[
                  { title: '절대 혼자 다이빙하지 않기', desc: '버디가 생명을 구합니다' },
                  { title: '과호흡 금지', desc: 'CO2 감각을 둔화시켜 위험합니다' },
                  { title: '자신의 한계 존중', desc: '무리한 목표 설정하지 않기' },
                  { title: '충분한 수면 휴식', desc: '다이빙 시간의 2배 이상 휴식' },
                ].map((item, idx) => (
                  <div key={idx} className={`p-3 xxs:p-2 rounded-lg ${isDark ? 'bg-green-900/20' : 'bg-green-50'}`}>
                    <p className={`font-semibold text-sm xxs:text-xs ${isDark ? 'text-green-300' : 'text-green-800'}`}>
                      ✅ {item.title}
                    </p>
                    <p className={`text-xs xxs:text-[10px] ${isDark ? 'text-green-200/80' : 'text-green-700'}`}>
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* 버디 찾기 CTA */}
            <div className={`p-5 xxs:p-4 rounded-xl ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
              <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-blue-300' : 'text-gray-900'}`}>
                안전한 버디 찾기
              </h3>
              <p className={`text-sm xxs:text-xs mb-4 xxs:mb-3 ${isDark ? 'text-blue-200/80' : 'text-gray-600'}`}>
                블랙아웃 대처를 아는 경험 있는 버디와 함께 다이빙하세요.
              </p>
              <Link
                href="/matching/new"
                className="inline-block px-5 xxs:px-4 py-2.5 xxs:py-2 text-sm xxs:text-xs font-medium bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                버디 매칭 신청
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>
  )
}
